import type { MyReview, PublicReview, RatingSummary, ReviewBlock } from '@capsico/contracts';
import type { MyReviewCardView, ReviewCardView } from '../components/Reviews';
import { canStillEdit, textLines } from '../hooks/reviewRules';
import { orderTime } from '../utils/format';
import type { Translate } from './index';

/** "Terrible" … "Loved it" under the star picker (null before a pick). */
export function starCaption(t: Translate, stars: number): string | null {
  if (stars < 1 || stars > 5) return null;
  return t(`rate.star${stars}` as 'rate.star1');
}

export function blockCopy(t: Translate, block: ReviewBlock): { title: string; body: string } {
  return { title: t('rate.blockTitle'), body: t(`rate.block.${block}`) };
}

export function publicReviewView(t: Translate, review: PublicReview): ReviewCardView {
  return {
    id: review.id,
    author: review.author,
    when: orderTime(review.createdAt),
    stars: review.foodStars,
    starsLabel: t('reviews.stars', { n: review.foodStars }),
    text: review.text,
    textLines: textLines(review.text),
    photos: review.photos,
    liked:
      review.likedDishes.length > 0
        ? t('reviews.liked', { dishes: review.likedDishes.join(', ') })
        : null,
    reply: review.reply ? { title: t('reviews.reply'), text: review.reply.text } : null,
  };
}

export function myReviewView(t: Translate, review: MyReview, now: number): MyReviewCardView {
  const editable = canStillEdit(review, now);
  const status =
    review.status === 'held'
      ? { text: t('my.status.held'), tone: 'warning' as const }
      : review.status === 'hidden'
        ? { text: t('my.status.hidden'), tone: 'neutral' as const }
        : review.status === 'removed'
          ? { text: t('my.status.removed'), tone: 'negative' as const }
          : null;
  return {
    id: review.id,
    outletName: review.outletName,
    meta: `${t('my.order', { number: review.orderNo })} · ${orderTime(review.createdAt)}`,
    food: {
      label: t('rate.foodShort'),
      stars: review.foodStars,
      aria: t('rate.starAria', { n: review.foodStars }),
    },
    delivery:
      review.deliveryStars !== null
        ? {
            label: t('rate.deliveryShort'),
            stars: review.deliveryStars,
            aria: t('rate.starAria', { n: review.deliveryStars }),
          }
        : null,
    text: review.text,
    textLines: textLines(review.text),
    photos: review.photos,
    status,
    reply: review.reply ? { title: t('reviews.reply'), text: review.reply.text } : null,
    edit: editable
      ? {
          label: t('common.edit'),
          note: t('rate.editUntil', { time: orderTime(review.editableUntil) }),
        }
      : null,
  };
}

/** Rating summary card copy; `fallbackNew` covers a summary that has not loaded yet. */
export function summaryCopy(t: Translate, summary: RatingSummary) {
  return {
    title: t('reviews.title'),
    average: summary.average === null ? null : summary.average.toFixed(1),
    isNew: summary.isNew,
    newLabel: t('reviews.new'),
    newBody: t('reviews.newBody'),
    countText: t('reviews.count', { count: summary.count }),
    distribution: summary.distribution,
  };
}

export function hygieneLabel(t: Translate, badge: 'green' | 'amber'): string {
  return t(badge === 'green' ? 'reviews.hygiene.green' : 'reviews.hygiene.amber');
}
